import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  GlobalStyles,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Security,
  VerifiedUser,
  FiberManualRecord,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * ── DESIGN TOKENS ─────────────────────────────────────────────────────────
 * Same as Login page - locked to light appearance with premium aesthetic
 */
const palette = {
  ink: '#0B1220',
  inkSoft: '#2A3342',
  muted: '#5B6472',
  faint: '#8A93A3',
  paper: '#FBFAF7',
  card: '#FFFFFF',
  border: '#E7E3D8',
  ledger: '#0E1B2E',
  ledgerLine: 'rgba(255,255,255,0.08)',
  teal: '#0F766E',
  tealDark: '#0B5C56',
  tealSoft: 'rgba(15,118,110,0.10)',
  red: '#DC2626',
  green: '#16A34A',
};

const lightOnlyTheme = createTheme({
  palette: {
    mode: 'light',
    background: { default: palette.paper, paper: palette.card },
    text: { primary: palette.ink, secondary: palette.muted },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
});

export default function ChangePassword() {
  const isMobile = useMediaQuery('(max-width:960px)');
  const navigate = useNavigate();
  const { user, logout, refreshTenant } = useAuth();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '1rem',
      color: palette.ink,
      bgcolor: palette.card,
      '& input': { py: 1.85 },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${palette.card} inset`,
        WebkitTextFillColor: palette.ink,
      },
      '& fieldset': { borderColor: palette.border },
      '&:hover fieldset': { borderColor: '#C7C1B0' },
      '&.Mui-focused fieldset': { borderColor: palette.teal, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { color: palette.muted, fontSize: '0.95rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: palette.tealDark },
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.new_password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/v1/auth/change-password', {
        current_password: formData.current_password,
        new_password: formData.new_password,
      });

      // Pick up the cleared temporary-password flag, so the app stops sending this user back here.
      await refreshTenant().catch(() => null);
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ledger = [
    { time: '09:41:03', vendor: 'Meridian Cloud Services', delta: -12, tag: 'CVE-2026-11284 disclosed' },
    { time: '09:38:47', vendor: 'Northwind Payments', delta: 4, tag: 'SOC 2 Type II renewed' },
    { time: '09:22:15', vendor: 'Argosy Data Labs', delta: -6, tag: 'TLS cert expires in 14d' },
    { time: '09:05:52', vendor: 'Halcyon Logistics', delta: -3, tag: 'Subprocessor added' },
  ];

  return (
    <ThemeProvider theme={lightOnlyTheme}>
      <GlobalStyles
        styles={{
          '@import':
            "url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap')",
          ':root': { colorScheme: 'light' },
          html: { colorScheme: 'light' },
          body: { colorScheme: 'light', backgroundColor: palette.paper },
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          bgcolor: palette.paper,
          colorScheme: 'light',
        }}
      >
        {/* ─── LEFT PANEL — "Live Ledger" ────────────────────────────────── */}
        {!isMobile && (
          <Box
            sx={{
              flex: '1 1 52%',
              maxWidth: '52%',
              bgcolor: palette.ledger,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { md: 6, lg: 9 },
              py: 6,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Grid texture */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                maskImage: 'radial-gradient(ellipse 75% 55% at 25% 15%, black, transparent)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -180,
                left: -140,
                width: 480,
                height: 480,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${palette.tealSoft} 0%, transparent 70%)`,
                filter: 'blur(6px)',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 520 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(15,118,110,0.16)',
                  border: '1px solid rgba(94,234,212,0.25)',
                  borderRadius: '999px',
                  px: 1.6,
                  py: 0.5,
                  mb: 4.5,
                }}
              >
                <FiberManualRecord sx={{ fontSize: 8, color: '#5EEAD4' }} />
                <Typography
                  sx={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11.5,
                    letterSpacing: '0.08em',
                    color: '#99F6E4',
                    fontWeight: 500,
                  }}
                >
                  SECURE PASSWORD CHANGE
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 500,
                  color: '#F5F4F0',
                  fontSize: { md: 33, lg: 40 },
                  lineHeight: 1.16,
                  letterSpacing: '-0.01em',
                  mb: 2.5,
                }}
              >
                Change your password to secure your account.
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(226,232,240,0.72)',
                  fontSize: 16,
                  lineHeight: 1.7,
                  mb: 5,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {user?.email ? (
                  <>Welcome <strong style={{ color: '#F5F4F0' }}>{user?.name || 'User'}</strong>! This is your first login. Please create a strong password to secure your account.</>
                ) : (
                  'This is your first login. Please create a strong password to secure your account.'
                )}
              </Typography>

              {/* Ledger table */}
              <Box
                sx={{
                  border: `1px solid ${palette.ledgerLine}`,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  mb: 5,
                  bgcolor: 'rgba(255,255,255,0.02)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: 2.2,
                    py: 1.2,
                    borderBottom: `1px solid ${palette.ledgerLine}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10.5,
                      letterSpacing: '0.09em',
                      color: 'rgba(226,232,240,0.45)',
                    }}
                  >
                    TIMESTAMP
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10.5,
                      letterSpacing: '0.09em',
                      color: 'rgba(226,232,240,0.45)',
                    }}
                  >
                    Δ SCORE
                  </Typography>
                </Box>

                {ledger.map((row, i) => (
                  <Box
                    key={row.vendor}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.2,
                      py: 1.3,
                      borderTop: i === 0 ? 'none' : `1px solid ${palette.ledgerLine}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11.5,
                          color: 'rgba(226,232,240,0.4)',
                          flexShrink: 0,
                        }}
                      >
                        {row.time}
                      </Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#F1F0EC',
                            fontSize: 13.5,
                            fontWeight: 500,
                            lineHeight: 1.25,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {row.vendor}
                        </Typography>
                        <Typography sx={{ color: 'rgba(226,232,240,0.45)', fontSize: 11.5 }}>
                          {row.tag}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0, pl: 1.5 }}>
                      <Typography
                        sx={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 13,
                          fontWeight: 500,
                          color: row.delta < 0 ? '#F87171' : '#4ADE80',
                        }}
                      >
                        {row.delta > 0 ? `+${row.delta}` : row.delta}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${palette.ledgerLine}`,
                    borderRadius: '999px',
                    px: 1.4,
                    py: 0.6,
                  }}
                >
                  <Security sx={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: 12 }}>
                    End-to-End Encrypted
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.7,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${palette.ledgerLine}`,
                    borderRadius: '999px',
                    px: 1.4,
                    py: 0.6,
                  }}
                >
                  <VerifiedUser sx={{ fontSize: 14, color: 'rgba(226,232,240,0.6)' }} />
                  <Typography sx={{ color: 'rgba(226,232,240,0.75)', fontSize: 12 }}>
                    Secure Password Change
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ─── RIGHT PANEL — Change Password Form ──────────────────────────── */}
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '1 1 48%' },
            maxWidth: { xs: '100%', md: '48%' },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 5 },
            bgcolor: palette.paper,
            minHeight: '100vh',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 480,
              p: 0,
              borderRadius: 3,
              bgcolor: 'transparent',
            }}
          >
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start', mb: isMobile ? 5 : 6 }}>
              <img
                src="/logo.png"
                alt="Platform logo"
                style={{ height: isMobile ? 84 : 108, width: 'auto' }}
              />
            </Box>

            <Box sx={{ mb: 5 }}>
              <Typography
                sx={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 500,
                  color: palette.ink,
                  fontSize: 30,
                  letterSpacing: '-0.01em',
                  mb: 1,
                }}
              >
                Change Your Password
              </Typography>
              <Typography sx={{ color: palette.muted, fontSize: 15.5 }}>
                {user?.email ? `For ${user.email}` : 'Set a new password for your account'}
              </Typography>
            </Box>

            {success ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: '#F0FDF4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <CheckCircle sx={{ fontSize: 40, color: palette.green }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: "'Fraunces', serif",
                    fontWeight: 500,
                    color: palette.ink,
                    fontSize: 24,
                    mb: 1,
                  }}
                >
                  Password Updated! 🎉
                </Typography>
                <Typography sx={{ color: palette.muted }}>
                  Your password has been changed successfully.
                  Redirecting to dashboard...
                </Typography>
                <CircularProgress sx={{ color: palette.teal, mt: 3 }} size={28} />
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                    {error}
                  </Alert>
                )}

                <Typography
                  component="label"
                  htmlFor="current-password"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.inkSoft, mb: 0.8 }}
                >
                  Current Password
                </Typography>
                <TextField
                  id="current-password"
                  fullWidth
                  name="current_password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.current_password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3, ...inputSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: palette.faint, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? (
                            <VisibilityOff sx={{ color: palette.faint, fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ color: palette.faint, fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  component="label"
                  htmlFor="new-password"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.inkSoft, mb: 0.8 }}
                >
                  New Password
                </Typography>
                <TextField
                  id="new-password"
                  fullWidth
                  name="new_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.new_password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 3, ...inputSx }}
                  helperText="Minimum 6 characters"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: palette.faint, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ color: palette.faint, fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ color: palette.faint, fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography
                  component="label"
                  htmlFor="confirm-password"
                  sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.inkSoft, mb: 0.8 }}
                >
                  Confirm Password
                </Typography>
                <TextField
                  id="confirm-password"
                  fullWidth
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 4, ...inputSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: palette.faint, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ color: palette.faint, fontSize: 20 }} />
                          ) : (
                            <Visibility sx={{ color: palette.faint, fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  disableElevation
                  sx={{
                    py: 1.7,
                    borderRadius: '8px',
                    bgcolor: palette.teal,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: palette.tealDark, boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: '#D7D2C4', color: '#fff' },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 15.5,
                  }}
                >
                  {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Change Password'}
                </Button>
              </form>
            )}
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}